import { useRef, useLayoutEffect, useState } from 'react';
import moment from 'moment';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { appendTimeZone } from '@s20/common/util/date';

const DAY_FORMAT = 'YYYY-MM-DD';

// Custom event component for month and agenda views
export const CustomEvent = ({
    event, view, slotStart, slotEnd,
    isFocusedEvent, setFocusedEvent, continuesPrior,
}) => {
    const jData = event.jobData?.[0] || {};

    const startTime = appendTimeZone(
        moment(event.start).format('hh:mm A'),
        jData.startTimeOffset,
        jData.jobLocation?.timeZone,
        jData.jobDate,
        jData.startTime,
    );

    // In month view, paint each day of the run individually: red for cancelled
    // days, blue for the rest. The gradient is applied to the parent .rbc-event
    // bar (the definitively-sized element) rather than the inner .custom-event,
    // so it fills the full height of the run regardless of the wrapper elements
    // react-big-calendar / drag-and-drop insert between the two. Pixel bands are
    // sized off the parent's rendered width so they align with this week-row
    // segment's day cells.
    const runRef = useRef(null);
    const [hasGradient, setHasGradient] = useState(false);

    useLayoutEffect(() => {
        const eventEl = runRef.current?.closest('.rbc-event');

        const clear = () => {
            if (eventEl) {
                eventEl.style.background = '';
                eventEl.style.borderWidth = '';
            }
            setHasGradient(false);
        };

        if (view !== 'month' || !event.cancelledDates?.length || !eventEl) {
            clear();

            return undefined;
        }

        const { width } = eventEl.getBoundingClientRect();

        // Days belonging to this week-row segment (an event can span multiple rows).
        const jobDays = event.jobData
            .filter((job) => moment(job.jobDate) >= slotStart && moment(job.jobDate) < slotEnd)
            .map((job) => job.jobDate);

        const spanDays = jobDays.length;

        if (spanDays < 1 || width <= 0) {
            clear();

            return undefined;
        }

        const pxPerDay = width / spanDays;
        const cancelled = new Set(event.cancelledDates.map((d) => moment(d).format(DAY_FORMAT)));

        // Shift the whole gradient left, and expand each band's right edge, so the
        // bands line up with the day-cell edges.
        const LEFT_SHIFT_PX = 2;
        const RIGHT_EXPAND_PX = 1;

        const stops = jobDays.map((day, dayIndex) => {
            const color = cancelled.has(moment(day).format(DAY_FORMAT)) ? 'var(--danger)' : 'var(--success)';
            const start = pxPerDay * dayIndex - LEFT_SHIFT_PX;
            const end = pxPerDay * (dayIndex + 1) - LEFT_SHIFT_PX + RIGHT_EXPAND_PX;

            return `${color} ${start}px, ${color} ${end}px`;
        });

        // Imperative on the parent: getEventStyles' returned style object has no
        // `background`/`border*` keys, so React never sets/clears them and won't clobber.
        eventEl.style.background = `linear-gradient(to right, ${stops.join(', ')})`;
        // Drop the border entirely so the gradient fills the whole bar edge-to-edge —
        // otherwise the .selected class's 1px var(--success) border leaves a blue strip
        // peeking past the red on the left/right (and top/bottom) edges.
        eventEl.style.borderWidth = '0';
        setHasGradient(true);

        return () => {
            eventEl.style.background = '';
            eventEl.style.borderWidth = '';
        };
    }, [event, view, slotStart, slotEnd]);

    return (
        <>
            <div
                ref={runRef}
                slotStart={slotStart}  // eslint-disable-line
                slotEnd={slotEnd}  // eslint-disable-line
                className={cls('custom-event', `job-${event.sessionId}`, {
                    // When the per-day gradient is active it lives on the parent
                    // .rbc-event bar, so the child stays transparent — skip the flat
                    // selected/cancelled classes, otherwise 'cancelled' would draw a
                    // solid-red bg + border over the gradient for the whole run.
                    'selected': !hasGradient && !event.isCancelled && !event.isGroup,
                    'cancelled': !hasGradient && event.isCancelled,
                })}
            >
                <div className="event-content">
                    {view === 'agenda' && <span className="event-time">{startTime}</span>}
                    <span className="event-title">{view === 'agenda' ? ` - ${event.title}` : event.title}</span>
                </div>
            </div>

            {Boolean(isFocusedEvent) && view === 'month' && !continuesPrior && (
                <button
                    type="button"
                    className="rbc-show-more"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        setFocusedEvent(null);
                    }}
                >
                    <FontAwesomeIcon
                        icon={faTimes}
                        className="mr-1"
                        size="sm"
                    />

                    Remove Focus
                </button>
            )}
        </>
    );
};